import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import { Google } from "@/components/icons/google";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { isApiError, useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // The guard stashes the interrupted location here. Accept only a relative
  // in-app path: anything absolute would be an open redirect.
  const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const returnPath =
    from?.pathname && from.pathname.startsWith("/") && !from.pathname.startsWith("//")
      ? `${from.pathname}${from.search ?? ""}`
      : null;
  const [searchParams] = useSearchParams();
  const loginMutation = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const isLoading = loginMutation.isPending;
  const canSubmit = !!email.trim() && !!password.trim() && !isLoading;

  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmitRef = useRef<(() => void) | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    logger.debug("[login] submitting for", email);
    try {
      await loginMutation.mutateAsync({ email, password });
      // Return to whatever the guard interrupted, defaulting to the home page.
      // Only in-app paths are honoured, so a crafted state cannot bounce the
      // user off-site after login.
      const target = returnPath ?? "/home";
      logger.debug("[login] success → redirecting to", target);
      navigate(target, { replace: true });
    } catch (err) {
      const status = isApiError(err) ? err.status : undefined;
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      if (status === 403 && detail === "email_not_verified") {
        logger.debug("[login] email not verified → redirecting to /verify-email");
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      if (status === 403 && detail === "domain_not_allowed") {
        logger.debug("[login] email domain not allowed");
        setError("This email domain isn't allowed to sign in.");
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  const handleGoogleLogin = () => {
    logger.debug("[login] redirecting to Google SSO");
    // Carry the interrupted destination through SSO as well, otherwise a deep
    // link survives a password login but is lost on the Google path.
    const next = returnPath ? `?next=${encodeURIComponent(returnPath)}` : "";
    window.location.assign(`/api/auth/google/login${next}`);
  };

  useEffect(() => {
    handleSubmitRef.current = () => {
      formRef.current?.requestSubmit();
    };
  });

  useEffect(() => {
    const oauthError = searchParams.get("error");
    if (!oauthError) {
      return;
    }
    logger.debug("[login] google sso error:", oauthError);
    setError(
      oauthError === "domain_not_allowed"
        ? "Only @boardofinnovation.com Google accounts can sign in."
        : "Google sign-in failed. Please try again.",
    );
  }, [searchParams]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
        e.preventDefault();
        handleSubmitRef.current?.();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canSubmit]);

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-primary text-2xl font-normal">Scaffold UI</CardTitle>
          <p className="text-muted text-sm">Sign in to continue</p>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="w-full"
          >
            <Google className="size-4" />
            Continue with Google
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-card text-muted px-2 text-xs uppercase">or</span>
            </div>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-foreground text-sm font-medium">
                  Password
                </label>
                <Link to="/forgot-password" className="text-accent text-sm hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-accent text-accent-foreground hover:bg-accent/90 w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <span className="text-muted-foreground ml-1 hidden text-xs sm:inline">⌘↵</span>
                </>
              )}
            </Button>

            <p className="text-muted text-center text-sm">
              Don&apos;t have an account?{" "}
              <Link to="/signup" className="text-accent hover:underline">
                Sign up
              </Link>
            </p>
          </form>

          <p className="text-muted mt-4 text-center text-xs">
            You&apos;ll stay signed in for 30 days on this device. Signing out of Google
            doesn&apos;t sign you out here, so sign out when using a shared computer.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
